import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Snackbar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config';

function Comments() {
  const [comments, setComments] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedComment, setSelectedComment] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyData, setReplyData] = useState({ name: '', email: '', content: '' });

  const fetchComments = async () => {
    try {
      const res = await fetch(getApiUrl('/api/comments'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('خطا در دریافت نظرات');
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await fetch(getApiUrl('/api/comments/pending/count'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  useEffect(() => {
    fetchComments();
    fetchPendingCount();
  }, []);

  const handleApprove = async (id) => {
    try {
      const res = await fetch(getApiUrl(`/api/comments/${id}/approve`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        toast.success('نظر تایید شد');
        fetchComments();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error approving comment:', error);
      toast.error('خطا در تایید نظر');
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await fetch(getApiUrl(`/api/comments/${id}/reject`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        toast.success('نظر رد شد');
        fetchComments();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error rejecting comment:', error);
      toast.error('خطا در رد نظر');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این نظر اطمینان دارید؟')) return;
    
    try {
      const res = await fetch(getApiUrl(`/api/comments/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        toast.success('نظر حذف شد');
        fetchComments();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('خطا در حذف نظر');
    }
  };

  const handleViewComment = (comment) => {
    setSelectedComment(comment);
    setViewDialogOpen(true);
  };

  const handleReply = (comment) => {
    setSelectedComment(comment);
    setReplyData({ name: '', email: '', content: '' });
    setReplyDialogOpen(true);
  };

  const handleSubmitReply = async () => {
    if (!replyData.name || !replyData.email || !replyData.content) {
      toast.error('لطفاً تمام فیلدها را پر کنید');
      return;
    }

    try {
      const res = await fetch(getApiUrl('/public/comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          post_id: selectedComment.post_id,
          parent_id: selectedComment.id,
          name: replyData.name,
          email: replyData.email,
          content: replyData.content
        })
      });

      if (res.ok) {
        toast.success('پاسخ با موفقیت ارسال شد');
        setReplyDialogOpen(false);
        setReplyData({ name: '', email: '', content: '' });
        fetchComments();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ارسال پاسخ');
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('خطا در ارسال پاسخ');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        مدیریت نظرات
        {pendingCount > 0 && (
          <Chip 
            label={`${pendingCount} نظر در انتظار تایید`} 
            color="warning" 
            sx={{ ml: 2 }}
          />
        )}
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>نام</TableCell>
              <TableCell>ایمیل</TableCell>
              <TableCell>محتوا</TableCell>
              <TableCell>تاریخ</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comments.map((comment) => (
              <TableRow key={comment.id}>
                <TableCell>{comment.name}</TableCell>
                <TableCell>{comment.email}</TableCell>
                <TableCell>
                  {comment.content.length > 50 
                    ? `${comment.content.substring(0, 50)}...` 
                    : comment.content
                  }
                </TableCell>
                <TableCell>{formatDate(comment.created_at)}</TableCell>
                <TableCell>
                  <Chip 
                    label={comment.is_approved ? 'تایید شده' : 'در انتظار تایید'} 
                    color={comment.is_approved ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleViewComment(comment)}>
                    <VisibilityIcon />
                  </IconButton>
                  {!comment.is_approved && (
                    <IconButton onClick={() => handleApprove(comment.id)}>
                      <CheckCircleIcon color="success" />
                    </IconButton>
                  )}
                  {!comment.is_approved && (
                    <IconButton onClick={() => handleReject(comment.id)}>
                      <CancelIcon color="error" />
                    </IconButton>
                  )}
                  <IconButton onClick={() => handleReply(comment)}>
                    <Button size="small" variant="outlined">پاسخ</Button>
                  </IconButton>
                  <IconButton onClick={() => handleDelete(comment.id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Comment Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>مشاهده نظر</DialogTitle>
        <DialogContent>
          {selectedComment && (
            <Box>
              <Typography variant="h6">نام: {selectedComment.name}</Typography>
              <Typography variant="body1">ایمیل: {selectedComment.email}</Typography>
              <Typography variant="body1">تاریخ: {formatDate(selectedComment.created_at)}</Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                محتوا: {selectedComment.content}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>پاسخ به نظر</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="نام"
            value={replyData.name}
            onChange={(e) => setReplyData({ ...replyData, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="ایمیل"
            value={replyData.email}
            onChange={(e) => setReplyData({ ...replyData, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="پاسخ"
            multiline
            rows={4}
            value={replyData.content}
            onChange={(e) => setReplyData({ ...replyData, content: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>لغو</Button>
          <Button onClick={handleSubmitReply} variant="contained">ارسال پاسخ</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Comments; 